/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  /** Primary line shown in the trigger and the option row. */
  label: string;
  /** Optional secondary line (e.g. plate number, email). */
  sublabel?: string;
  /** Extra text matched against the search query but not displayed. */
  keywords?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
}

/**
 * Lightweight searchable single-select combobox. Same look & behavior as
 * StateSelect but driven by an arbitrary option list, so it can back any
 * "pick one from many, filter by typing" field (cars, drivers, …).
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results',
  disabled = false,
  allowClear = true,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropUp(spaceBelow < 260);
      }
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      o.label.toLowerCase().includes(q) ||
      (o.sublabel || '').toLowerCase().includes(q) ||
      (o.keywords || '').toLowerCase().includes(q)
    );
  }, [search, options]);

  const selected = options.find(o => o.value === value) || null;

  const handleSelect = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring flex items-center justify-between gap-1 text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
          {selected ? (
            <>
              {selected.label}
              {selected.sublabel && <span className="ml-1 text-gray-400">{selected.sublabel}</span>}
            </>
          ) : placeholder}
        </span>
        <div className="flex items-center gap-0.5 shrink-0">
          {allowClear && value && !disabled && (
            <span onClick={handleClear} className="p-0.5 hover:bg-gray-100 rounded cursor-pointer">
              <X className="h-3 w-3 text-gray-400" />
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
        </div>
      </button>

      {open && (
        <div className={`absolute z-50 w-full rounded-md border bg-white shadow-lg ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}>
          <div className="p-1.5 border-b">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-7 px-2 text-sm rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">{emptyMessage}</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleSelect(o.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent cursor-pointer ${
                    o.value === value ? 'bg-accent font-medium' : ''
                  }`}
                >
                  <div className="truncate">{o.label}</div>
                  {o.sublabel && <div className="truncate text-xs text-gray-400">{o.sublabel}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
